import { memo, useEffect } from "react";
import { Box, Grid2 } from "@mui/material";
import { useQuickWesentlichkeit } from "@services/hooks/useQuickWesentlichkeit";
import { useNavigate } from "react-router-dom";
import { RouteTo } from "@routing/RouteMap";
import AIServicesNavbar from "../components/AIServicesNavbar";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification } from "@/utils/errorHandling";
import JobSubmissionForm from "@services/components/JobSubmissionForm";
import { FETCH_COMPANY } from "@company/graphql/queries/fetchCompany";
import { useQuery } from "@apollo/client";
import { Query } from "@/app/graphql/codegen";

/**
 * Formular-Seite zum Einreichen einer Quick-Wesentlichkeitsanalyse.
 *
 * ⚠️ Die aktuell erfassten Formdaten werden noch nicht an das Backend übergeben.
 *    Nach erfolgreicher Einreichung erfolgt eine Weiterleitung zur Status-Seite.
 */
const WesentlichkeitServicePage = memo(() => {
    const { submitJob, loading, error } = useQuickWesentlichkeit();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Fetch company to get ID for uploads
    const { data: companyData } = useQuery<Pick<Query, "company">>(FETCH_COMPANY);
    const companyId = companyData?.company?.id || "";

    // Zeige Benachrichtigung, wenn beim Einreichen ein Fehler auftritt
    useEffect(() => {
        if (error) {
            handleErrorWithNotification(
                error as Error,
                dispatch,
                "Fehler beim Einreichen",
                "Die Analyse konnte nicht gestartet werden. Bitte versuchen Sie es erneut."
            );
        }
    }, [error, dispatch]);

    const handleSubmit = async () => {
        const job = await submitJob([]);
        if (job?.id) {
            navigate(`${RouteTo.AI_SERVICES}/wesentlichkeit/status/${job.id}`);
        } else {
            navigate(`${RouteTo.AI_SERVICES}/wesentlichkeit/status/current`);
        }
    };

    // UI ist stark an bestehende WesentlichkeitsSettings-Komponente angelehnt, aber unabhängig davon umgesetzt
    return (
        <Box sx={{ p: 3 }}>
            <Grid2 container spacing={3}>
                {/* Sidebar */}
                <Grid2 size={{ xs: 12, md: 3 }}>
                    <AIServicesNavbar />
                </Grid2>

                {/* Main Content */}
                <Grid2 size={{ xs: 12, md: 9 }}>
                    <JobSubmissionForm
                        title="Quick-Wesentlichkeitsanalyse"
                        description="Führe mit dem ESGbot in nur wenigen Minuten eine schnelle doppelte Wesentlichkeitsanalyse durch. Diese dient als solide Basis für Ihren Nachhaltigkeitsbericht, spart viel Zeit und liefert präzise Ergebnisse."
                        loading={loading}
                        submitLabel="Analyse durchführen"
                        onSubmit={handleSubmit}
                        companyId={companyId}
                    />
                </Grid2>
            </Grid2>
        </Box>
    );
});

export default WesentlichkeitServicePage; 